import { useCallback, useEffect, useRef } from "react";

import cashRegisterSound from "../assets/cash-register.mp3";

const VOLUME = 0.85;

/*
  The last batch we played, at module scope on purpose: the feed
  unmounts whenever the dashboard toggles to the invite view, and a
  component ref would reset with it, so remounting would replay the
  most recent donation's sound.
*/
let lastPlayedKey = null;

/*
  Cash register sound for donation arrivals.

  Browsers block audio until the page has seen a user gesture, so we
  attempt a silent unlock on the first pointerdown/keydown. On a TV
  dashboard that gesture may never come — every play is best-effort
  and failures are swallowed, since sound is non-critical.
*/
export default function useDonationSound() {
  const audioRef = useRef(null);

  const setup = useCallback(() => {
    if (audioRef.current) return audioRef.current;

    const audio = new Audio(cashRegisterSound);
    audio.preload = "auto";
    audio.volume = VOLUME;
    audioRef.current = audio;

    return audio;
  }, []);

  useEffect(() => {
    const unlock = () => {
      const audio = setup();
      if (!audio) return;

      /*
        Muted for the unlock: play() resolves once playback has begun,
        so the pause below always lands a beat late. Unmuted, that beat
        is an audible fragment of the cash register — a phantom second
        ring if the gesture happens near a real arrival.
      */
      audio.muted = true;

      const stop = () => {
        audio.pause();
        audio.currentTime = 0;
        audio.muted = false;
      };

      const unlockPromise = audio.play();

      if (unlockPromise && typeof unlockPromise.then === "function") {
        unlockPromise.then(stop).catch(() => {
          // Ignore browser autoplay restrictions.
          audio.muted = false;
        });
      } else {
        stop();
      }

      document.removeEventListener("pointerdown", unlock);
      document.removeEventListener("keydown", unlock);
    };

    document.addEventListener("pointerdown", unlock);
    document.addEventListener("keydown", unlock);

    return () => {
      document.removeEventListener("pointerdown", unlock);
      document.removeEventListener("keydown", unlock);
    };
  }, [setup]);

  /*
    Pass the arrival's batch id as `key` to make the call idempotent:
    the same batch can ask to be played any number of times and is only
    ever heard once. Omit it to force a play.
  */
  return useCallback(
    (key) => {
      if (key != null) {
        if (key === lastPlayedKey) return;
        lastPlayedKey = key;
      }

      const audio = setup();
      if (!audio) return;

      try {
        // Clone so overlapping arrivals don't cut each other off.
        const instance = audio.cloneNode(true);
        instance.volume = VOLUME;
        instance.muted = false;

        const playPromise = instance.play();

        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {
            /* Blocked until the page receives a user interaction. */
          });
        }
      } catch (error) {
        /* Sound is non-critical. */
      }
    },
    [setup],
  );
}
